import { Box, Chip, Divider, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const fmtEuro = value =>
  `${Number(value || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// Human-readable names for the RBAC roles the wage table is keyed by.
const ROLE_LABELS = {
  MarketingManager: 'Marketing-Leitung',
  ITManager: 'IT-Leitung',
  B2BManager: 'B2B-Leitung',
  AssistantITManager: 'Stellv. IT-Leitung',
  ccManager: 'Callcenter-Leitung',
  hrManager: 'HR-Leitung',
  executiveLeadership: 'Geschäftsführung',
  managementEmployee: 'Management',
  accountingEmployee: 'Buchhaltung',
  ITEmployee: 'IT',
  marketingEmployee: 'Marketing',
  b2bEmployee: 'B2B',
  ccEmployee: 'Callcenter-Mitarbeiter',
  branchManager: 'Arenaleitung',
  branchEmployee: 'Kundenbetreuung',
  axeEmployee: 'Axe',
};

/**
 * Marks personnel-cost figures as an estimate and explains, on hover, exactly which assumptions
 * produced them. Everything comes from the backend's `meta.assumptions`, so the wage table lives
 * in one place instead of being duplicated per view — and the backend scopes it: the on-site
 * statistic publishes only the roles it actually prices.
 *
 * Renders nothing when the payload carries no assumptions — an older cached statistic simply
 * shows no notice rather than a misleading empty one.
 */
export default function StaffCostEstimateNotice({ assumptions, sx }) {
  if (!assumptions || !assumptions.isEstimate) return null;

  const roles = Array.isArray(assumptions.roleDefaults) ? assumptions.roleDefaults : [];
  const coverage = assumptions.coverage || {};
  const hasRealWages = Number(coverage.assignedWagePercent) > 0;
  // The on-site statistic prices every worked hour identically — it has no overtime split and
  // converts no annual salary, so neither explanation belongs in its tooltip.
  const appliesOvertime = assumptions.appliesOvertime !== false;

  const tooltip = (
    <Box sx={{ p: 0.5, maxWidth: 420 }}>
      <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.5 }}>
        Womit gerechnet wird
      </Typography>
      {assumptions.scopeNote && (
        <Typography variant='caption' sx={{ display: 'block', mb: 1 }}>
          {assumptions.scopeNote}
        </Typography>
      )}
      <Typography variant='caption' sx={{ display: 'block', mb: 1 }}>
        Ist bei einem Mitarbeiter ein Stundenlohn hinterlegt, wird dieser verwendet. Sonst gilt der
        Standardsatz seiner Rolle:
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 1.5, rowGap: 0.25 }}>
        {roles.map(role => (
          <Box key={role.role} sx={{ display: 'contents' }}>
            <Typography variant='caption'>
              {ROLE_LABELS[role.role] || role.role}
              {appliesOvertime && role.overtimeCompensated ? ' (Überstunden abgegolten)' : ''}
            </Typography>
            <Typography variant='caption' sx={{ textAlign: 'right', fontWeight: 600 }}>
              {fmtEuro(role.wage)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 1 }} />

      <Typography variant='caption' sx={{ display: 'block' }}>
        Arbeitgeber-Faktor <strong>{assumptions.employerCostFactor}</strong> auf den Bruttolohn
        (Sozialabgaben und Nebenkosten).
      </Typography>
      {appliesOvertime && (
        <Typography variant='caption' sx={{ display: 'block' }}>
          Jahresgehälter mit {assumptions.annualHoursBasis} h/Jahr umgerechnet,{' '}
          {assumptions.weeklyNormalHours} h/Woche als Normalarbeitszeit.
        </Typography>
      )}
      <Typography variant='caption' sx={{ display: 'block', mt: 0.5 }}>
        {hasRealWages
          ? `${coverage.assignedWagePercent} % der Schichten im Zeitraum beruhen auf einem hinterlegten Lohn, der Rest auf diesen Standardsätzen.`
          : 'Im Zeitraum ist bei keinem Mitarbeiter ein individueller Stundenlohn hinterlegt — die Kosten beruhen vollständig auf den Standardsätzen.'}
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement='bottom-start'>
      <Chip
        size='small'
        color='warning'
        variant='outlined'
        icon={<InfoOutlinedIcon />}
        label='Schätzung'
        sx={{ cursor: 'help', ...sx }}
      />
    </Tooltip>
  );
}
